/**
 * Console log backend for development.
 *
 * Writes structured JSON to console.log (for events) and console.error (for errors).
 * Useful for local development and debugging.
 */

import type { BaseLogEntry } from "../types";
import type { LogBackend } from "./interface";
import { logDevError } from "./log-dev-error";

/**
 * Console backend implementation.
 * Outputs formatted JSON to the browser/server console.
 */
export class ConsoleLogBackend implements LogBackend {
  /**
   * Write a log entry to the console.
   *
   * Events without errors go to console.log.
   * Events with errors go to console.error.
   *
   * @param entry - The log entry to write
   */
  write(entry: BaseLogEntry): void {
    try {
      const formatted = {
        ...entry,
        // Convert timestamp to ISO string for readability
        _time: new Date(entry.timestamp).toISOString(),
      };

      if (entry.error) {
        const errorSummary = `${entry.error.type}: ${entry.error.message}`;
        console.error(`[${entry.eventName}] ${errorSummary}`, formatted);
      } else {
        console.log(`[${entry.eventName}]`, formatted);
      }
    } catch (err) {
      // Silent fail - logging should never break the app
      logDevError("ConsoleLogBackend", "write", err);
    }
  }
}
